/**
 * S-CP10-4 — Timing Defense Gate persistence
 *
 * Writes deal_timing_records rows and updates account_plan_mpact denorms.
 * Supabase is canonical; we do a fire-and-forget write and console.error
 * on failure (matching Sub3/Sub4 patterns).
 *
 * NOTE: deal_timing_records.deal_id is uuid. The current opp model uses
 * string IDs in account_plan_mpact rows — caller must pass a uuid string.
 * For the smoke-demo path (no Supabase row yet) the store falls back to a
 * localStorage cache so the gate is exercisable end-to-end in dev.
 */

import { getSession, getCurrentTenant, supabase } from "./session";
import type { DeclaredTimingButton, SoftStopFlag, TimingAnswer, TimingBand } from "./cp10-timing-band";

type ToastFn = (message: string) => void;
let externalToast: ToastFn | null = null;
export function setTimingToastHandler(fn: ToastFn | null): void {
  externalToast = fn;
}
function surface(message: string, err: unknown): void {
  console.error("[cp10-timing-store]", message, err);
  if (externalToast) externalToast(message);
}

const LS_KEY = "aip:cp10-timing:v1";

export interface DealTimingRecord {
  id?: string;
  deal_id: string;
  account_id: number | null;
  close_date: string;
  cp10_timing_trigger_event: TimingAnswer["q1_trigger_event"] | null;
  cp10_timing_customer_confirmed: TimingAnswer["q2_customer_confirmed"] | null;
  cp10_timing_paper_runway: TimingAnswer["q3_paper_runway"] | null;
  cp10_timing_slip_history: TimingAnswer["q4_slip_history"] | null;
  system_band_lower: number | null;
  system_band_upper: number | null;
  system_band_label: string | null;
  soft_stop_flags: SoftStopFlag[];
  rep_declared_timing_pct: number | null;
  overcall_justification: string | null;
  overcall_flag_sent: boolean;
  undercall_confirmed: boolean;
  effective_timing_pct: number | null;
  blended_forecast_pct: number | null;
  is_current: boolean;
  assessed_at: string;
  assessed_by: string | null;
}

export interface SaveTimingInput {
  deal_id: string;
  account_id: number;
  close_date: string;
  answers: TimingAnswer;
  band: TimingBand;
  declared_button: DeclaredTimingButton;
  declared_pct: number;
  overcall_justification: string | null;
  overcall_flag_sent: boolean;
  undercall_confirmed: boolean;
  blended_forecast_pct: number | null;
}

function lsAll(): DealTimingRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function lsWrite(rows: DealTimingRecord[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(rows));
  } catch (err) {
    surface("Could not cache timing record locally", err);
  }
}

export function getCurrentTimingRecord(deal_id: string): DealTimingRecord | null {
  return lsAll().find((r) => r.deal_id === deal_id && r.is_current) ?? null;
}

export async function saveTimingAssessment(input: SaveTimingInput): Promise<DealTimingRecord> {
  const user = getSession();
  const tenant = getCurrentTenant();

  const record: DealTimingRecord = {
    deal_id: input.deal_id,
    account_id: input.account_id,
    close_date: input.close_date,
    cp10_timing_trigger_event: input.answers.q1_trigger_event,
    cp10_timing_customer_confirmed: input.answers.q2_customer_confirmed,
    cp10_timing_paper_runway: input.answers.q3_paper_runway,
    cp10_timing_slip_history: input.answers.q4_slip_history,
    system_band_lower: input.band.lower,
    system_band_upper: input.band.upper,
    system_band_label: input.band.label,
    soft_stop_flags: input.band.soft_stop_flags,
    rep_declared_timing_pct: input.declared_pct,
    overcall_justification: input.overcall_justification,
    overcall_flag_sent: input.overcall_flag_sent,
    undercall_confirmed: input.undercall_confirmed,
    effective_timing_pct: input.declared_pct,
    blended_forecast_pct: input.blended_forecast_pct,
    is_current: true,
    assessed_at: new Date().toISOString(),
    assessed_by: user?.id ?? null,
  };

  // Local write-through cache (mark prior records inactive)
  const rows = lsAll().map((r) =>
    r.deal_id === input.deal_id ? { ...r, is_current: false } : r,
  );
  rows.push(record);
  lsWrite(rows);

  // Fire-and-forget Supabase write
  if (user && tenant) {
    void (async () => {
      try {
        const sb = supabase();
        // Mark prior records inactive
        await sb
          .from("deal_timing_records")
          .update({ is_current: false })
          .eq("deal_id", input.deal_id)
          .eq("is_current", true);
        const { error } = await sb.from("deal_timing_records").insert({
          deal_id: input.deal_id,
          account_id: input.account_id,
          close_date: input.close_date,
          cp10_timing_trigger_event: record.cp10_timing_trigger_event,
          cp10_timing_customer_confirmed: record.cp10_timing_customer_confirmed,
          cp10_timing_paper_runway: record.cp10_timing_paper_runway,
          cp10_timing_slip_history: record.cp10_timing_slip_history,
          system_band_lower: record.system_band_lower,
          system_band_upper: record.system_band_upper,
          system_band_label: record.system_band_label,
          soft_stop_flags: record.soft_stop_flags,
          rep_declared_timing_pct: record.rep_declared_timing_pct,
          overcall_justification: record.overcall_justification,
          overcall_flag_sent: record.overcall_flag_sent,
          overcall_flag_sent_at: record.overcall_flag_sent ? record.assessed_at : null,
          undercall_confirmed: record.undercall_confirmed,
          effective_timing_pct: record.effective_timing_pct,
          blended_forecast_pct: record.blended_forecast_pct,
          tenant_id: tenant.id,
          assessed_by: user.id,
          is_current: true,
        });
        if (error) surface("Could not save timing record to Supabase", error);
      } catch (err) {
        surface("Could not save timing record to Supabase", err);
      }
    })();
  }

  return record;
}

export interface ManagerSoftStopFlagPayload {
  deal_id: string;
  flag_type: "TIMING_OVERCALL";
  rep_declared_pct: number;
  system_band_lower: number;
  system_band_upper: number;
  justification_text: string;
  close_date: string;
  rep_id: string;
}

export async function sendManagerSoftStopFlag(payload: ManagerSoftStopFlagPayload): Promise<void> {
  console.info("[cp10-timing-store] SOFT_STOP flag → manager queue", payload);
  // Real wire: POST /api/manager/soft-stop-flags (out of scope for this build).
  // For dev smoke we log to console; integrator wires the existing SOFT_STOP
  // pipeline used by other CP flags.
}

export type TimingAuditEvent =
  | "TIMING_GATE_COMPLETED"
  | "TIMING_OVERCALL_JUSTIFICATION_SUBMITTED"
  | "TIMING_UNDERCALL_CONFIRMED";

export async function writeTimingAuditLog(
  event_type: TimingAuditEvent,
  deal_id: string,
  payload: Record<string, unknown>,
): Promise<void> {
  console.info(`[cp10-timing-store] audit: ${event_type}`, { deal_id, ...payload });
  // Integrator: wire to existing deal_audit_log table once available.
}
